import { Injectable } from '@angular/core';
import { User as FirebaseAuthUser } from '@angular/fire/auth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  from,
  map,
  mergeMap,
  of,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { AuthInfo, UserProfile } from '../../models/user-profile.model';
import { AppState } from '../app.state';
import { mapAuthErrorToMessage } from './helpers/auth-error-mapper';
import { AuthService } from './services/auth.service';
import { UserProfileService } from './services/user-profile.service';
import {
  appleLogin,
  changeEmail,
  changeEmailFailure,
  changeEmailSuccess,
  changePassword,
  changePasswordFailure,
  changePasswordSuccess,
  googleLogin,
  initAuth,
  loadUserProfileFailure,
  loadUserProfileSuccess,
  login,
  loginFailure,
  loginSuccess,
  logout,
  logoutSuccess,
  register,
  registerFailure,
  resetPassword,
  resetPasswordFailure,
  resetPasswordSuccess,
  updateUserProfile,
  updateUserProfileFailure,
  updateUserProfileSuccess,
} from './user.actions';
import { selectUserProfile } from './user.selectors';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private userProfileService: UserProfileService,
    private navigationService: NavigationService,
    private store: Store<AppState>
  ) {}

  private async createAuthInfo(user: FirebaseAuthUser): Promise<AuthInfo> {
    let isAdmin = false;

    try {
      const idTokenResult = await user.getIdTokenResult();
      isAdmin = !!idTokenResult.claims['admin'];
    } catch (error) {
      console.error('Failed to read admin claim from ID token:', error);
    }

    return {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      isAnonymous: user.isAnonymous,
      isAdmin,
    };
  }

  // Initialize auth state when app starts
  initAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initAuth),
      mergeMap(() =>
        this.authService.getAuthState().pipe(
          take(1),
          mergeMap((firebaseUser) => {
            if (firebaseUser) {
              return from(this.createAuthInfo(firebaseUser)).pipe(
                map((authInfo) => loginSuccess({ authInfo, source: 'rehydrate' }))
              );
            } else {
              return of(logoutSuccess());
            }
          }),
          catchError((error) => {
            console.error('Auth initialization error:', error);
            return of(logoutSuccess());
          })
        )
      )
    )
  );

  // Handle user login
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      mergeMap(({ email, password }) =>
        this.authService.loginWithEmailAndPassword(email, password).pipe(
          mergeMap((user) =>
            from(this.createAuthInfo(user)).pipe(
              map((authInfo) => loginSuccess({ authInfo, source: 'login' }))
            )
          ),
          catchError((error) =>
            of(loginFailure({ error: mapAuthErrorToMessage(error.code) }))
          )
        )
      )
    )
  );

  // Handle user registration
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(register),
      mergeMap(({ email, password }) =>
        this.authService.createUserWithEmailAndPassword(email, password).pipe(
          mergeMap((user) =>
            from(this.createAuthInfo(user)).pipe(
              map((authInfo) => loginSuccess({ authInfo, source: 'login' }))
            )
          ),
          catchError((error) =>
            of(registerFailure({ error: mapAuthErrorToMessage(error.code) }))
          )
        )
      )
    )
  );

  // Handle Google login
  googleLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(googleLogin),
      mergeMap(() =>
        this.authService.googleLogin().pipe(
          mergeMap((user) =>
            from(this.createAuthInfo(user)).pipe(
              map((authInfo) => loginSuccess({ authInfo, source: 'login' }))
            )
          ),
          catchError((error) =>
            of(
              loginFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  // Handle Apple login
  appleLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appleLogin),
      mergeMap(() =>
        this.authService.appleLogin().pipe(
          mergeMap((user) =>
            from(this.createAuthInfo(user)).pipe(
              map((authInfo) => loginSuccess({ authInfo, source: 'login' }))
            )
          ),
          catchError((error) =>
            of(loginFailure({ error: mapAuthErrorToMessage(error.code) }))
          )
        )
      )
    )
  );

  // Handle logout
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      mergeMap(() =>
        this.authService.signOut().pipe(
          map(() => logoutSuccess()),
          tap(() => this.navigationService.navigateToWelcome()),
          catchError((error) => {
            console.error('Logout failed:', error);
            return of(logoutSuccess());
          })
        )
      )
    )
  );

  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserProfile),
      withLatestFrom(this.store.select(selectUserProfile)),
      mergeMap(([{ updates }, currentProfile]) => {
        if (!currentProfile) {
          return of(
            updateUserProfileFailure({ error: 'No user profile found' })
          );
        }

        const updatesWithUid = { ...updates, uid: currentProfile.uid };

        return this.userProfileService.updateUserProfile(updatesWithUid).pipe(
          map(() => {
            const updatedUserProfile = { ...currentProfile, ...updatesWithUid };
            return updateUserProfileSuccess({
              userProfile: updatedUserProfile,
            });
          }),
          catchError((error) => of(updateUserProfileFailure({ error })))
        );
      })
    )
  );

  // After successful login or rehydration, populate and sync the user profile
  handleUserProfileAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      mergeMap(({ authInfo }) =>
        // First try to get the existing profile
        this.userProfileService.getUserProfile(authInfo.uid).pipe(
          mergeMap((existingProfile) => {
            if (existingProfile) {
              // User exists - update auth fields that might have changed
              const updatedProfile: Partial<UserProfile> = {
                uid: authInfo.uid,
                email: authInfo.email,
                displayName: authInfo.displayName,
                photoURL: authInfo.photoURL,
                isAnonymous: authInfo.isAnonymous,
                // TODO: Refactor these object creations into helper functions
                // DO NOT ADD ANYTHING ELSE HERE
                // New fields should be added below in the new user section
              };

              // Check if any auth fields have changed
              const hasChanges = Object.keys(updatedProfile).some((key) => {
                const typedKey = key as keyof UserProfile;
                return (
                  typedKey !== 'uid' &&
                  updatedProfile[typedKey] !== existingProfile[typedKey]
                );
              });

              if (hasChanges) {
                // Auth data changed, update the profile first
                return this.userProfileService
                  .updateUserProfile(updatedProfile)
                  .pipe(
                    mergeMap(() => {
                      // Then load the updated profile
                      const updatedUser = {
                        ...existingProfile,
                        ...updatedProfile,
                      };
                      return of(
                        loadUserProfileSuccess({ userProfile: updatedUser })
                      );
                    }),
                    catchError((error) =>
                      of(loadUserProfileFailure({ error: error.message }))
                    )
                  );
              } else {
                // No changes needed, just return existing profile
                return of(
                  loadUserProfileSuccess({ userProfile: existingProfile })
                );
              }
            } else {
              // New user - create complete profile with defaults
              const newUserProfile: UserProfile = {
                uid: authInfo.uid,
                email: authInfo.email,
                displayName: authInfo.displayName,
                photoURL: authInfo.photoURL,
                isAnonymous: authInfo.isAnonymous,
                account_startedAt: new Date(),
                account_deletionRequestedAt: null,
                premium_waitlistEnrolled: false,
                features_visibleFeatures: [],
              };

              // Create the new profile
              return this.userProfileService
                .updateUserProfile(newUserProfile)
                .pipe(
                  map(() =>
                    loadUserProfileSuccess({ userProfile: newUserProfile })
                  ),
                  catchError((error) =>
                    of(loadUserProfileFailure({ error: error.message }))
                  )
                );
            }
          }),
          catchError((error) =>
            of(loadUserProfileFailure({ error: error.message }))
          )
        )
      )
    )
  );

  // Navigate to home after login
  navigateAfterProfileLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserProfileSuccess),
        withLatestFrom(this.actions$.pipe(ofType(loginSuccess))),
        filter(([, loginAction]) => loginAction.source === 'login'),
        tap(() => this.navigationService.navigateToHome())
      ),
    { dispatch: false }
  );

  // Handle password reset
  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPassword),
      mergeMap(({ email }) =>
        this.authService.sendPasswordResetEmail(email).pipe(
          map(() => resetPasswordSuccess({ email })),
          catchError((error) =>
            of(
              resetPasswordFailure({ error: mapAuthErrorToMessage(error.code) })
            )
          )
        )
      )
    )
  );

  // Effect for changing password
  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePassword),
      mergeMap(({ currentPassword, newPassword }) =>
        this.authService.changePassword(currentPassword, newPassword).pipe(
          map(() => changePasswordSuccess()),
          catchError((error) =>
            of(
              changePasswordFailure({
                error: mapAuthErrorToMessage(error.message),
              })
            )
          )
        )
      )
    )
  );

  // Effect for changing email
  changeEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeEmail),
      mergeMap(({ currentPassword, newEmail }) =>
        this.authService.changeEmail(currentPassword, newEmail).pipe(
          map(() => changeEmailSuccess()),
          catchError((error) =>
            of(
              changeEmailFailure({
                error: mapAuthErrorToMessage(error.message),
              })
            )
          )
        )
      )
    )
  );
}
